import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { saveCase, getCaseByReference, updateCase } from './dynamodb';

function FirstPortCase() {
  const [mode, setMode] = useState('create');
  const [formData, setFormData] = useState({
    caseReference: '',
    houseNumber: '',
    streetName: '',
    timeRaised: '',
    description: '',
    closureDetails: '',
    closureDate: ''
  });
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('success');
  const [modalMessage, setModalMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchReference, setSearchReference] = useState('');
  const [caseFound, setCaseFound] = useState(false);

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!captchaValue) {
      setModalType('error');
      setModalMessage('Please complete the CAPTCHA verification.');
      setShowModal(true);
      return;
    }
    
    setIsSubmitting(true);
    try {
      await saveCase({
        caseReference: formData.caseReference,
        houseNumber: formData.houseNumber,
        streetName: formData.streetName,
        timeRaised: formData.timeRaised,
        description: formData.description
      });
      setModalType('success');
      setModalMessage('Case created successfully!');
      setShowModal(true);
      setFormData({ caseReference: '', houseNumber: '', streetName: '', timeRaised: '', description: '', closureDetails: '', closureDate: '' });
      setCaptchaValue(null);
    } catch (error) {
      setModalType('error');
      setModalMessage('Failed to create case. Please try again.');
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearchCase = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const caseData = await getCaseByReference(searchReference);
      if (caseData) {
        setFormData({
          caseReference: caseData.caseReference,
          houseNumber: caseData.houseNumber,
          streetName: caseData.streetName,
          timeRaised: caseData.timeRaised,
          description: caseData.description || '',
          closureDetails: caseData.closureDetails || '',
          closureDate: caseData.closureDate || ''
        });
        setCaseFound(true);
      } else {
        setModalType('error');
        setModalMessage('Case not found. Please check the reference number.');
        setShowModal(true);
      }
    } catch (error) {
      setModalType('error');
      setModalMessage('Error retrieving case. Please try again.');
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!captchaValue) {
      setModalType('error');
      setModalMessage('Please complete the CAPTCHA verification.');
      setShowModal(true);
      return;
    }
    
    setIsSubmitting(true);
    try {
      await updateCase(formData.caseReference, {
        closureDetails: formData.closureDetails,
        closureDate: formData.closureDate
      });
      setModalType('success');
      setModalMessage('Case updated successfully!');
      setShowModal(true);
      setCaseFound(false);
      setSearchReference('');
      setFormData({ caseReference: '', houseNumber: '', streetName: '', timeRaised: '', description: '', closureDetails: '', closureDate: '' });
      setCaptchaValue(null);
    } catch (error) {
      setModalType('error');
      setModalMessage('Failed to update case. Please try again.');
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setCaseFound(false);
    setSearchReference('');
    setFormData({ caseReference: '', houseNumber: '', streetName: '', timeRaised: '', description: '', closureDetails: '', closureDate: '' });
    setCaptchaValue(null);
  };

  return (
    <div className="survey-container">
      <div className="survey-pattern"></div>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8 col-md-10 col-12">
            <div className="modern-form p-5 fade-in">
              <div className="text-center">
                <div className="survey-icon mx-auto" style={{background: '#2563eb'}}>📋</div>
                <h1 className="survey-title">Report a case logged by a resident with firstport</h1>
                <p className="survey-subtitle">Create and manage First Port cases</p>
              </div>

              <div className="d-flex gap-2 mb-4">
                <button 
                  className={`btn ${mode === 'create' ? 'btn-primary-custom' : 'btn-outline-custom'} flex-fill`}
                  onClick={() => switchMode('create')}
                >
                  Report Case
                </button>
                <button 
                  className={`btn ${mode === 'update' ? 'btn-primary-custom' : 'btn-outline-custom'} flex-fill`}
                  onClick={() => switchMode('update')}
                >
                  Update Case
                </button>
              </div>

              {mode === 'create' ? (
                <form onSubmit={handleCreateSubmit}>
                  <div className="mb-4">
                    <label className="form-label">Case Reference *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={formData.caseReference}
                      onChange={(e) => setFormData({...formData, caseReference: e.target.value})}
                      required 
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">House Number *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={formData.houseNumber}
                      onChange={(e) => setFormData({...formData, houseNumber: e.target.value})}
                      required 
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Street Name *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={formData.streetName}
                      onChange={(e) => setFormData({...formData, streetName: e.target.value})}
                      required 
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Time Case Raised *</label>
                    <input 
                      type="datetime-local" 
                      className="form-control" 
                      value={formData.timeRaised}
                      onChange={(e) => setFormData({...formData, timeRaised: e.target.value})}
                      required 
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Case Description *</label>
                    <textarea 
                      className="form-control" 
                      rows="4" 
                      placeholder="Enter details about the case..."
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                    ></textarea>
                  </div>

                  <div className="mb-4 d-flex justify-content-center">
                    <ReCAPTCHA
                      sitekey="6Ldifl0pAAAAADZCvzJSAm-wBdsmx0flhew4zXhT"
                      onChange={setCaptchaValue}
                      size="compact"
                    />
                  </div>

                  <div className="text-center mt-4">
                    <button type="submit" className="btn btn-primary-custom" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating...' : 'Create Case'}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {!caseFound ? (
                    <form onSubmit={handleSearchCase}>
                      <div className="mb-4">
                        <label className="form-label">Enter Case Reference *</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={searchReference}
                          onChange={(e) => setSearchReference(e.target.value)}
                          placeholder="Enter case reference to retrieve details"
                          required 
                        />
                      </div>

                      <div className="text-center mt-4">
                        <button type="submit" className="btn btn-primary-custom" disabled={isSubmitting}>
                          {isSubmitting ? 'Searching...' : 'Retrieve Case'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <form onSubmit={handleUpdateSubmit}>
                      <div className="alert-custom mb-4">
                        <strong>Case Found:</strong> {formData.caseReference}
                      </div>

                      <div className="mb-4">
                        <label className="form-label">House Number</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={formData.houseNumber}
                          disabled
                        />
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Street Name</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={formData.streetName}
                          disabled
                        />
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Time Case Raised</label>
                        <input 
                          type="datetime-local" 
                          className="form-control" 
                          value={formData.timeRaised}
                          disabled
                        />
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Case Description</label>
                        <textarea 
                          className="form-control" 
                          rows="4" 
                          value={formData.description}
                          disabled
                        ></textarea>
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Closure Date *</label>
                        <input 
                          type="date" 
                          className="form-control" 
                          value={formData.closureDate}
                          onChange={(e) => setFormData({...formData, closureDate: e.target.value})}
                          required
                        />
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Closure Details *</label>
                        <textarea 
                          className="form-control" 
                          rows="5" 
                          placeholder="Enter closure details..."
                          value={formData.closureDetails}
                          onChange={(e) => setFormData({...formData, closureDetails: e.target.value})}
                          required
                        ></textarea>
                      </div>

                      <div className="mb-4 d-flex justify-content-center">
                        <ReCAPTCHA
                          sitekey="6Ldifl0pAAAAADZCvzJSAm-wBdsmx0flhew4zXhT"
                          onChange={setCaptchaValue}
                          size="compact"
                        />
                      </div>

                      <div className="text-center mt-4 d-flex gap-2 justify-content-center">
                        <button 
                          type="button" 
                          className="btn btn-outline-custom" 
                          onClick={() => setCaseFound(false)}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-primary-custom" disabled={isSubmitting}>
                          {isSubmitting ? 'Updating...' : 'Update Case'}
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {showModal && (
        <div className="success-modal" onClick={() => setShowModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className={`success-icon ${modalType === 'error' ? 'error-icon' : ''}`}>
              {modalType === 'success' ? '✓' : '⚠'}
            </div>
            <h3 className="mb-3">
              {modalType === 'success' ? 'Success!' : 'Error'}
            </h3>
            <p className="text-muted mb-4">{modalMessage}</p>
            <button className="btn btn-primary-custom" onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FirstPortCase;
